import { useEffect, useMemo } from "react";

import { API } from "@js/api";
import { Snackbar } from "@js/components/snackbar";
import { useAppDispatch, useNavigate } from "@js/hooks";
import { isErrorWithDataAndRootMessage } from "@js/types/errors";
import type { EmployerBidListBid } from "@js/types/jobs";

import { useFetchBidsQuery } from "../../api";
import { useBidListFiltersMapped } from "../use-bid-list-filters-mapped";

const defaultBidList: EmployerBidListBid[] = [];

export const useFilteredBidList = ({
  jobId,
  refetchOnMountOrArgChange,
  isInterviewingTab,
}: {
  jobId: number | undefined;
  refetchOnMountOrArgChange?: boolean;
  isInterviewingTab: boolean;
}) => {
  const { bidFilters, areFiltersDefault } = useBidListFiltersMapped();

  const filters = useMemo(() => {
    if (isInterviewingTab && !bidFilters.status) {
      return {
        ...bidFilters,
        status: [
          ENUMS.BidStatus.INTERVIEWING,
          ENUMS.BidStatus.OFFER_MADE,
          ENUMS.BidStatus.OFFER_DECLINED,
          ENUMS.BidStatus.OFFER_CANCELED,
          ENUMS.BidStatus.HIRED,
          ENUMS.BidStatus.IN_REVIEW_AFTER_AI_INTERVIEW,
          ENUMS.BidStatus.LIVE_INTERVIEW_AFTER_AI_INTERVIEW,
        ].join(","),
      };
    }
    return bidFilters;
  }, [bidFilters, isInterviewingTab]);

  const {
    data,
    isLoading: isBidListLoading,
    isFetching: isBidListFetching,
    error,
  } = useFetchBidsQuery(
    { job: jobId, ...filters },
    { refetchOnMountOrArgChange, skip: !jobId },
  );

  useEffect(() => {
    if (isErrorWithDataAndRootMessage(error)) {
      Snackbar.error(error.data._error);
    } else if (error) {
      Snackbar.error("Something went wrong, please try different filters.");
    }
  }, [error]);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const resetFilters = () => {
    navigate(window.location.pathname);
  };

  const bidList = !!data && data.results ? data.results : defaultBidList;

  return {
    bidList,
    bidFilters,
    areFiltersDefault,
    isBidListLoading,
    isBidListFetching,
    refetchBidList: () => {
      dispatch(
        API.util.invalidateTags([
          { type: "EmployerBids", id: "LIST" },
          { type: "Jobs", id: jobId },
        ]),
      );
    },
    total: data?.count || 0,
    isLastPage: !data?.next,
    resetFilters,
  };
};
